/**
 * Persona identity components — avatar, inline display, shared utilities.
 *
 * Deterministic color from name so the same persona always looks the same.
 * Three sizes: sm (list items), md (cards), lg (detail hero).
 */

import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// ── Color palette ──────────────────────────────────────────────

// Editorial hues — muted chroma, warm-leaning, AAA with white text.
// Lightness held near 0.40 so all 8 read as the same visual weight.
const AVATAR_COLORS = [
  "bg-[oklch(0.42_0.09_35)] text-white", // clay
  "bg-[oklch(0.44_0.07_75)] text-white", // ochre
  "bg-[oklch(0.42_0.06_150)] text-white", // sage
  "bg-[oklch(0.40_0.06_200)] text-white", // teal
  "bg-[oklch(0.40_0.07_240)] text-white", // slate
  "bg-[oklch(0.38_0.09_280)] text-white", // indigo
  "bg-[oklch(0.40_0.08_330)] text-white", // plum
  "bg-[oklch(0.42_0.09_15)] text-white", // rose
];

export function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

// ── PersonaAvatar ──────────────────────────────────────────────

const sizeStyles = {
  // typography-ignore: 16px mini avatar initials require 9px to fit 2 characters
  xs: "h-4 w-4 text-[9px] rounded-xs",
  sm: "h-6 w-6 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-lg",
} as const;

interface PersonaAvatarProps {
  name: string;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

export function PersonaAvatar({
  name,
  size = "md",
  className,
}: PersonaAvatarProps) {
  const initials = getInitials(name);
  const colorClass = avatarColor(name);

  // Deterministic crease angle based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const foldType = Math.abs(hash) % 3;

  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg font-semibold shadow-xs select-none",
        sizeStyles[size],
        colorClass,
        className,
      )}
      aria-label={name}
    >
      {/* Origami Facet Overlay */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full opacity-35"
        viewBox="0 0 40 40"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {foldType === 0 && (
          <>
            <polygon
              points="0,0 40,0 25,40 0,40"
              fill="#ffffff"
              opacity="0.3"
            />
            <polygon points="25,40 40,0 40,40" fill="#000000" opacity="0.25" />
            <line
              x1="40"
              y1="0"
              x2="25"
              y2="40"
              stroke="#ffffff"
              strokeWidth="1"
              opacity="0.6"
            />
          </>
        )}
        {foldType === 1 && (
          <>
            <polygon
              points="0,0 40,15 15,40 0,40"
              fill="#ffffff"
              opacity="0.25"
            />
            <polygon points="40,15 40,40 15,40" fill="#000000" opacity="0.3" />
            <line
              x1="0"
              y1="0"
              x2="40"
              y2="40"
              stroke="#ffffff"
              strokeWidth="1"
              opacity="0.6"
            />
          </>
        )}
        {foldType === 2 && (
          <>
            <polygon points="0,0 20,40 0,40" fill="#000000" opacity="0.25" />
            <polygon
              points="0,0 40,0 40,40 20,40"
              fill="#ffffff"
              opacity="0.2"
            />
            <line
              x1="0"
              y1="0"
              x2="20"
              y2="40"
              stroke="#ffffff"
              strokeWidth="1"
              opacity="0.5"
            />
          </>
        )}
      </svg>

      {/* Initials on top */}
      <span className="relative z-10 font-bold tracking-tight">{initials}</span>
    </span>
  );
}

// ── PersonaInline ──────────────────────────────────────────────

interface PersonaInlineProps {
  name: string;
  role?: string | null;
  size?: "sm" | "md";
  /** When set, name becomes a link to the persona detail page */
  link?: { projectId: string; personaId: string };
  className?: string;
}

export function PersonaInline({
  name,
  role,
  size = "sm",
  link,
  className,
}: PersonaInlineProps) {
  const nameEl = link ? (
    <Link
      to={`/projects/${link.projectId}/personas/${link.personaId}`}
      className="font-semibold hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  ) : (
    <span className="font-semibold">{name}</span>
  );

  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <PersonaAvatar name={name} size={size} />
      <span className={cn("min-w-0", size === "sm" ? "text-xs" : "text-sm")}>
        {nameEl}
        {role && <span className="text-muted-foreground"> · {role}</span>}
      </span>
    </span>
  );
}
